import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { matchAPI, walletAPI } from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { 
  Trophy, 
  Wallet, 
  Calendar, 
  MapPin, 
  Users, 
  ChevronRight,
  Plus,
  TrendingUp,
  TrendingDown,
  Clock
} from 'lucide-react';
import { formatCurrency, formatDate, formatTime, getEloRank, getMatchStatusBadge } from '../lib/utils';

export default function Dashboard() {
  const { user, refreshUser } = useAuth();
  const [matches, setMatches] = useState([]);
  const [upcomingMatch, setUpcomingMatch] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [matchesRes] = await Promise.all([
          matchAPI.getMy(),
        ]);
        
        const allMatches = matchesRes.data;
        setMatches(allMatches);
        
        // Find next upcoming match
        const now = new Date();
        const upcoming = allMatches
          .filter(m => m.status !== 'completed' && m.status !== 'cancelled')
          .filter(m => new Date(`${m.date}T${m.time}`) > now)
          .sort((a, b) => new Date(`${a.date}T${a.time}`) - new Date(`${b.date}T${b.time}`))[0];
        
        setUpcomingMatch(upcoming);
        await refreshUser();
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const eloRank = getEloRank(user?.elo_points || 1000);
  const winRate = user?.matches_played > 0 
    ? Math.round((user.wins / user.matches_played) * 100) 
    : 0;

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-8 w-48 mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-32 rounded-2xl" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8" data-testid="player-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="font-display font-bold text-xl sm:text-2xl md:text-3xl text-tunis-blue">
              Marhaba, {user?.name?.split(' ')[0]}! 👋
            </h1>
            <p className="text-muted-foreground text-sm sm:text-base mt-1">Bereit für ein Spiel?</p>
          </div>
          <Link to="/matches/create" className="w-full sm:w-auto">
            <Button className="btn-primary w-full sm:w-auto" data-testid="create-match-btn">
              <Plus className="w-5 h-5 mr-2" />
              Spiel erstellen
            </Button>
          </Link>
        </div>

        {/* Stats Grid - Bento Style */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          {/* ELO Card */}
          <Card className="col-span-1 bg-gradient-to-br from-tunis-blue to-tunis-blue/80 text-white border-0" data-testid="elo-card">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-2 sm:mb-4">
                <Trophy className="w-6 h-6 sm:w-8 sm:h-8 text-white/80" />
                <Badge className={`${eloRank.bg} ${eloRank.color} border-0 text-[10px] sm:text-xs`}>
                  {eloRank.name}
                </Badge>
              </div>
              <div className="font-display font-bold text-2xl sm:text-3xl">{user?.elo_points || 1000}</div>
              <div className="text-white/70 text-xs sm:text-sm">ELO Punkte</div>
            </CardContent>
          </Card>

          {/* Wallet Card */}
          <Card className="col-span-1 bg-gradient-to-br from-tunis-teal to-tunis-teal/80 text-white border-0" data-testid="wallet-card">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-2 sm:mb-4">
                <Wallet className="w-6 h-6 sm:w-8 sm:h-8 text-white/80" />
                <Link to="/wallet">
                  <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-white/80 hover:text-white" />
                </Link>
              </div>
              <div className="font-display font-bold text-xl sm:text-3xl">{formatCurrency(user?.wallet_balance || 0)}</div>
              <div className="text-white/70 text-xs sm:text-sm">Guthaben</div>
            </CardContent>
          </Card>

          {/* Matches Played */}
          <Card className="col-span-1 border-slate-200" data-testid="matches-card">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-2 sm:mb-4">
                <Calendar className="w-6 h-6 sm:w-8 sm:h-8 text-tunis-blue/60" />
              </div>
              <div className="font-display font-bold text-2xl sm:text-3xl text-tunis-blue">{user?.matches_played || 0}</div>
              <div className="text-muted-foreground text-xs sm:text-sm">Spiele gespielt</div>
            </CardContent>
          </Card>

          {/* Win Rate */}
          <Card className="col-span-1 border-slate-200" data-testid="winrate-card">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-2 sm:mb-4">
                {winRate >= 50 ? (
                  <TrendingUp className="w-6 h-6 sm:w-8 sm:h-8 text-green-500" />
                ) : (
                  <TrendingDown className="w-6 h-6 sm:w-8 sm:h-8 text-red-500" />
                )}
              </div>
              <div className="font-display font-bold text-2xl sm:text-3xl text-tunis-blue">{winRate}%</div>
              <div className="text-muted-foreground text-xs sm:text-sm">Siegquote</div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Upcoming Match - Wide Card */}
          <Card className="lg:col-span-2 border-slate-200 overflow-hidden" data-testid="upcoming-match-card">
            <CardHeader className="bg-slate-50 border-b border-slate-200">
              <CardTitle className="flex items-center gap-2 text-tunis-blue">
                <Clock className="w-5 h-5" />
                Nächstes Spiel
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {upcomingMatch ? (
                <div className="flex flex-col sm:flex-row gap-6">
                  <div className="flex-1 space-y-4">
                    <div className="flex items-center gap-2">
                      <Badge className={getMatchStatusBadge(upcomingMatch.status).className}>
                        {getMatchStatusBadge(upcomingMatch.status).text}
                      </Badge>
                    </div>
                    <h3 className="font-display font-bold text-xl text-tunis-blue">
                      {upcomingMatch.field_name}
                    </h3>
                    <div className="space-y-2 text-sm text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        {formatDate(upcomingMatch.date)} um {formatTime(upcomingMatch.time)}
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        {upcomingMatch.field_location}
                      </div>
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        {upcomingMatch.current_players}/{upcomingMatch.max_players} Spieler
                      </div>
                    </div>
                    <Link to={`/matches/${upcomingMatch.id}`}>
                      <Button className="btn-secondary" data-testid="view-match-btn">
                        Details ansehen
                        <ChevronRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </div>
                  <div className="sm:w-48 h-32 sm:h-auto bg-gradient-to-br from-tunis-blue/10 to-tunis-teal/10 rounded-xl flex items-center justify-center">
                    <Calendar className="w-16 h-16 text-tunis-blue/30" />
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4">Keine anstehenden Spiele</p>
                  <Link to="/matches">
                    <Button className="btn-primary" data-testid="find-match-btn">
                      Spiel finden
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="border-slate-200" data-testid="quick-actions-card">
            <CardHeader>
              <CardTitle className="text-tunis-blue">Schnellaktionen</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Link to="/matches" className="block">
                <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer">
                  <div className="w-10 h-10 bg-tunis-teal/10 rounded-xl flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-tunis-teal" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-tunis-blue">Spiele durchsuchen</div>
                    <div className="text-sm text-muted-foreground">Offene Spiele finden</div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </div>
              </Link>
              <Link to="/map" className="block">
                <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer">
                  <div className="w-10 h-10 bg-tunis-blue/10 rounded-xl flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-tunis-blue" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-tunis-blue">Karte öffnen</div>
                    <div className="text-sm text-muted-foreground">Felder in der Nähe</div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </div>
              </Link>
              <Link to="/leaderboard" className="block">
                <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer">
                  <div className="w-10 h-10 bg-amber-500/10 rounded-xl flex items-center justify-center">
                    <Trophy className="w-5 h-5 text-amber-500" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-tunis-blue">Rangliste</div>
                    <div className="text-sm text-muted-foreground">Top Spieler sehen</div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </div>
              </Link>
              <Link to="/wallet" className="block">
                <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer">
                  <div className="w-10 h-10 bg-green-500/10 rounded-xl flex items-center justify-center">
                    <Wallet className="w-5 h-5 text-green-500" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-tunis-blue">Guthaben aufladen</div>
                    <div className="text-sm text-muted-foreground">D17, Flouci, Karte</div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </div>
              </Link>
            </CardContent>
          </Card>
        </div>

        {/* Recent Matches */}
        {matches.length > 0 && (
          <Card className="mt-6 border-slate-200" data-testid="recent-matches-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-tunis-blue">Meine Spiele</CardTitle>
              <Link to="/matches/my">
                <Button variant="ghost" size="sm" className="text-tunis-teal">
                  Alle ansehen
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {matches.slice(0, 3).map((match) => (
                  <Link
                    key={match.id}
                    to={`/matches/${match.id}`}
                    className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors"
                    data-testid={`match-${match.id}`}
                  >
                    <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm">
                      <Calendar className="w-6 h-6 text-tunis-blue" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-tunis-blue truncate">{match.field_name}</div>
                      <div className="text-sm text-muted-foreground">
                        {formatDate(match.date)} • {formatTime(match.time)}
                      </div>
                    </div>
                    <Badge className={getMatchStatusBadge(match.status).className}>
                      {getMatchStatusBadge(match.status).text}
                    </Badge>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
